import {Controller} from "@hotwired/stimulus";
import flatpickr from "flatpickr";
import { parseTimeInput } from "./support/time_input";

export default class extends Controller {
    static values = {
        enableTime: {type: Boolean, default: false},
        noCalendar: {type: Boolean, default: false},
        dateFormat: {type: String, default: "F j, Y"},
        altInput: {type: Boolean, default: true},
        allowInput: {type: Boolean, default: true},
        altFormat: {type: String, default: "F j, Y"},
        minDate: {type: String, default: null},
        maxDate: {type: String, default: null},
        inline: {type: Boolean, default: false},
        disableMobile: {type: Boolean, default: true},
        placeholder: {type: String, default: ""},
        time24hr: {type: Boolean, default: false}, // optional
    };

    isMobile() {
        return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }

    connect() {
        this.mobile = this.isMobile();

        if (this.isMobile()) {
            this._installMobilePlaceholders(['#birthdayDateFirstChild', '#text-date', '#startTime', '#endTime']);
        }

        const options = {
            enableTime: this.enableTimeValue,
            noCalendar: this.noCalendarValue,
            dateFormat: this.dateFormatValue,
            altInput: this.altInputValue,
            allowInput: this.allowInputValue,
            altFormat: this.altFormatValue,
            minDate: this.noCalendarValue ? null : (this.minDateValue || null),
            maxDate: this.maxDateValue,
            inline: this.inlineValue,
            disableMobile: false,
            time_24hr: this.time24hrValue,
            // Invalid times must be filtered before flatpickr tries to format them.
            allowInvalidPreload: !this.noCalendarValue,
            locale: { firstDayOfWeek: 1 },
            onChange: () => {
                this.element.dispatchEvent(new Event("input", {bubbles: true}));
            },
            onOpen: () => {
                if (!this.mobile) {
                    document.body.style.overflow = "hidden";
                    document.querySelector(".offcanvas-body")?.style && (document.querySelector(".offcanvas-body").style.overflow = "hidden");
                }
            },
            onClose: () => {
                if (!this.mobile) {
                    document.body.style.overflow = "";
                    const ob = document.querySelector(".offcanvas-body");
                    if (ob) ob.style.overflow = "";
                }
            },
        };

        const rawVal = (this.element.value || '').trim();
        const isoDateRe = /^\d{4}-\d{2}-\d{2}$/;
        const isoDateTimeRe = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

        if (rawVal && (isoDateRe.test(rawVal) || isoDateTimeRe.test(rawVal))) {
            options.defaultDate = rawVal;
        }

        const dataDefault = this.element.getAttribute('data-default-date');
        if (!options.defaultDate && dataDefault) {
            options.defaultDate = dataDefault;
        }

        options.parseDate = (dateStr, format) => {
            if (!dateStr) return null;

            const s = String(dateStr).trim();

            // 1) Pure date: YYYY-MM-DD  (used for child birthdays, mobile date fields, etc.)
            if (isoDateRe.test(s)) {
                const [y, m, d] = s.split('-').map(n => parseInt(n, 10));
                // Local date at noon to avoid any DST / timezone off-by-one
                return new Date(y, m - 1, d, 12, 0, 0, 0);
            }

            // 2) ISO datetime: YYYY-MM-DDTHH:MM(:SS)?  (if we ever feed full party_start here)
            if (isoDateTimeRe.test(s)) {
                const [datePart, timePart] = s.split('T');
                const [y, m, d] = datePart.split('-').map(n => parseInt(n, 10));
                const [H, Mi, S = '0'] = timePart.split(':').map(n => parseInt(n, 10));
                return new Date(y, m - 1, d, H, Mi, S);
            }

            // Time-only inputs must not use flatpickr's permissive date parser:
            // shorthand such as "2pm" otherwise silently becomes 2:00 AM.
            if (this.noCalendarValue) {
                const time = parseTimeInput(s);
                if (!time) return undefined;
                const date = new Date();
                date.setHours(time.H, time.M, time.S, 0);
                return date;
            }

            // 3) For all OTHER strings (e.g. "Thursday, August 28, 2025", "6:30 PM"):
            //    use the old, forgiving behavior.
            const t = Date.parse(s);
            if (!Number.isNaN(t)) {
                return new Date(t); // relies on browser/local parsing for party date & times
            }

            // 4) As a final fallback, let flatpickr try with its own format
            try {
                return flatpickr.parseDate ? flatpickr.parseDate(s, format) : null;
            } catch (_) {
                return null;
            }
        };

        this.flatpickr = flatpickr(this.element, options);
    }

    disconnect() {
        this.flatpickr?.destroy();
    }


    _installMobilePlaceholders(selectors) {
        const ensureWrap = (el) => {
            if (el.parentElement?.classList.contains('ea-ph-wrap')) return el.parentElement;
            const wrap = document.createElement('div');
            wrap.className = 'ea-ph-wrap';
            el.parentElement.insertBefore(wrap, el);
            wrap.appendChild(el);
            return wrap;
        };

        const toggle = (el) => {
            if (el.value && String(el.value).trim() !== '') {
                el.classList.add('ea-ph-has-value');
            } else {
                el.classList.remove('ea-ph-has-value');
            }
        };

        selectors.forEach(sel => {
            const el = document.querySelector(sel);
            if (!el) return;

            const wrap = ensureWrap(el);

            // create label only once
            let label = wrap.querySelector('.ea-ph-label');
            if (!label) {
                label = document.createElement('span');
                label.className = 'ea-ph-label';
                label.textContent = el.getAttribute('placeholder') || '';
                wrap.appendChild(label);
            }

            // sync width so ellipsis behaves nicely
            const syncWidth = () => { label.style.maxWidth = `${el.clientWidth - 24}px`; };
            syncWidth();
            new ResizeObserver(syncWidth).observe(el);

            // initial + listeners
            toggle(el);
            ['input','change','blur'].forEach(evt => el.addEventListener(evt, () => toggle(el)));

            // Some pickers set value programmatically; poll briefly after mount
            setTimeout(() => toggle(el), 0);
            setTimeout(() => toggle(el), 250);
        });
    }

    open() {
        this.flatpickr?.open();
    }

    close() {
        this.flatpickr?.close();
    }

    setDate(event) {
        const dateToSet = event.params.date;
        if (this.flatpickr && dateToSet) {
            this.flatpickr.setDate(dateToSet, true);
        }
    }
}
